import { THEME, ThemeProvider } from '@vodacomsa/ns-react-components';
import {
  Form,
  Formik
} from 'formik';
import React, {
  useEffect,
  useMemo,
  useState
} from 'react';
import { formFields } from './BusinessDirectorConstants';
import {
  directorValidationSchema,
  validateDirectorIdNumber,
  isPartialDirector
} from './directorValidationSchema';
import { useLocation, useOutletContext } from 'react-router-dom';
import { DirectorDetails } from '../../../constants';
import {
  useRecoilValueLoadable,
  useRecoilCallback,
  useRecoilValue
} from 'recoil';
import { FormBuilder } from '../../../components/FormBuilder';
import {
  ErrorModalSection,
  FormSubmitHelper,
  SupportPopup,
  Loader
} from '../../../components';
import {
  CommonHeight,
  FormContainer,
  FormField
} from '../../../constants';
import {
  replaceUndefined
} from '../../../helpers';
import {
  useIsMobile,
  directorDetailsSubmit,
  updateDirectorDetails,
  businessDetailsSubmit
} from '../../../recoil';

import { useModal } from '../../../components/modalController/useModal';
import { findFirstValidationError } from '../../../helpers/formik';
import { useTealium } from '../../../tealium/useTealium';
import { ApplicationRoutes } from '../../../Router';

export const BusinessDirectorDetails = () => {

  const [openModal, closeModal] = useModal();
  const { submitState, setSubmitState, backClick, submissionStates, nextClick } = useOutletContext();
  const businessDetails = useRecoilValueLoadable(businessDetailsSubmit).valueMaybe();
  const businessIdToFetchDirector = businessDetails?.businessId;
  // (localStorage.getItem('isNewBusiness') === 'true') &&
  //   businessDetails?.sharedBusinessDirectors
  //   ? businessDetails?.sharedBusinessDirectors
  //   : businessDetails?.businessId;
  const { track, events } = useTealium();
  const location = useLocation();
  const isMobile = useRecoilValue(useIsMobile);
  const isBusinessVerified = !(businessDetails?.isBusinessVerified === false);
  const isSoleTrader = businessDetails?.businessType === 'Sole Proprietorship';
  let formTouched = false;

  const directorLoadable = useRecoilValueLoadable(
    directorDetailsSubmit(businessIdToFetchDirector)
  );

  const [directorDetails, setDirectorDetails] = useState(null);

  useEffect(() => {
    if (directorLoadable.state === 'hasValue' && !directorDetails) {
      setDirectorDetails(directorLoadable.contents);
    }
  }, [directorLoadable]);

  const handleFormFocus = () => {
    if (formTouched) {
      return;
    }

    formTouched = true;
    track(events.businessDirectors.formStart());
  };

  useEffect(() => {
    track(events.businessDirectors.pageView());
    sessionStorage.removeItem('regNumber');
    sessionStorage.removeItem('businessName');
    sessionStorage.removeItem('vatNumber');
  }, []);

  const maskIdNumber = (str) => {
    const unmaskedCharLength = 6;
    const visible = str?.slice(0, unmaskedCharLength);
    const masked = '#'.repeat(Math.max(0, str?.length - unmaskedCharLength));
    return visible + masked;
  };

  const formDetails = useMemo(() => {
    if (!directorDetails) {return { directors: [{ ...DirectorDetails}] };}

    const directors =
      directorDetails.directors?.length === 0
        ? [{ ...DirectorDetails }]
        : directorDetails?.directors?.map((v) => ({
          ...v,
          isIdVerified: true,
          maskedIdNumber: maskIdNumber(v.idNumber)
        }));

    return replaceUndefined({
      ...directorDetails,
      directors
    });
  }, [directorDetails]);

  const fetchedTotalDirectors = formDetails?.directors?.length || 0;

  const submitDetails = useRecoilCallback(({ set }) =>
    async (businessId, formData) => {

      const filtered = formData.directors.filter(d => !isPartialDirector(d));

      const payload = {
        directors: filtered.map(d => ({ ...d, isDeleted: false })),
        isBusinessVerified: false
      };

      const saved = await updateDirectorDetails(payload);
      set(directorDetailsSubmit(businessId), saved);
      setDirectorDetails(saved);
      return saved;
    }
  );

  const closeErrorModal = () => {
    setSubmitState(submissionStates.default);
    closeModal();
  };

  useEffect(() => {
    const data = directorDetails;

    if (data?.error) {
      openModal({
        title: 'Error',
        children: <ErrorModalSection
          traceId={data?.traceId}
          error={data?.error}
          onCloseErrorModal={closeErrorModal}
        />,
        type: 'error',
        onCloseModal: closeErrorModal
      });
      return;
    }

    if (submitState === submissionStates.submitting && !data?.error) {
      setSubmitState(submissionStates.submitSuccess);
    }
  }, [directorDetails]);

  const handleFormikTouchedErrors = (touched, errors) => {
    const errorMessage = findFirstValidationError(errors, touched);

    if (!errorMessage) {
      return;
    }

    track(events.error('form', location.pathname, errorMessage));
  };

  const onBackClick = () => {
    track(events.businessDirectors.formEnd('back'));
    window.location.pathname = ApplicationRoutes.businessDetails;
    backClick();
  };

  const onNextClick = async () => {
    track(events.businessDirectors.formEnd('next'));
    // const sharedBusinessId = businessDetails?.sharedBusinessDirectors;
    // localStorage.removeItem('isNewBusiness');
    // if (sharedBusinessId) {
    //   await submitDetails(businessIdToFetchDirector, formDetails);
    // }
    setSubmitState(submissionStates.submitSuccess);
    return nextClick;
  };

  if (!directorDetails) {
    return (
      <Loader />
    );
  }

  return (

    <div style={styles.container}>

      <ThemeProvider theme={THEME.revamp}>
        <Formik
          initialValues={formDetails}
          onSubmit={() => { }}
          validationSchema={directorValidationSchema}
          validateOnMount={true}
          validate={validateDirectorIdNumber}
          enableReinitialize={true}
        >
          {({ values, errors, touched }) => {
            handleFormikTouchedErrors(touched, errors);

            return (
              <Form style={FormContainer} onFocus={handleFormFocus}>
                <FormSubmitHelper submitState={submitState} setSubmitState={setSubmitState} />

                <FormBuilder
                  fields={formFields}
                  title="Director’s details"
                  subtitle='We need the company director details to process your business funding application.'
                  isMobile={isMobile}
                  values={values}
                  FormField={FormField}
                  errors={errors}
                  submitState={submitState}
                  setSubmitState={setSubmitState}
                  fetchedTotalDirectors={fetchedTotalDirectors}
                  backClick={onBackClick}
                  journeyType={'onBoarding'}
                  requireFooter={true}
                  isDetailsCaptured={directorDetails?.directors?.length > 0}
                  isBusinessVerified={isBusinessVerified}
                  isSoleTrader={isSoleTrader}
                  submissionStates={submissionStates}
                  nextClick={onNextClick}
                  touched={touched}
                  submitDirector={(fd) => submitDetails(businessIdToFetchDirector, fd)}
                  sharedBusinessIdDirector={(sessionStorage.getItem('isNewBusiness') === 'true') && businessDetails?.sharedBusinessDirectors}
                />
              </Form>
            );
          }}
        </Formik>
      </ThemeProvider>
      <SupportPopup />
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    minHeight: '50vh',
    margin: `0 0 ${CommonHeight.NavigationFooterHeightDesktop} 0`
  }
};
